import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { extractErrorMessage } from '../lib/errorUtils';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = ({ setUser }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('credentials'); // 'credentials' | '2fa'
  const [challengeToken, setChallengeToken] = useState('');
  const [code, setCode] = useState('');
  const codeInputRef = useRef(null);
  const { t, language, toggleLanguage } = useLanguage();

  useEffect(() => {
    if (step === '2fa') {
      setTimeout(() => codeInputRef.current?.focus(), 60);
    }
  }, [step]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });

      if (data.requires_2fa) {
        setChallengeToken(data.challenge_token);
        setCode('');
        setStep('2fa');
      } else {
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setUser(data.user);
        toast.success(t('login_success'));
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Login error'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2fa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/verify-login`, {
        challenge_token: challengeToken,
        code: code.trim(),
      });
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
      toast.success(t('login_success'));
    } catch (error) {
      toast.error(extractErrorMessage(error, t('login_2fa_bad_code')));
      setCode('');
      codeInputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative">
      <div className="absolute top-4 right-4 z-10 flex gap-2 p-2 rounded-md bg-white shadow-md" data-testid="login-language-switcher">
        <button
          onClick={() => toggleLanguage('it')}
          className={`text-2xl transition-all ${language === 'it' ? 'ring-2 ring-blue-500 rounded' : 'opacity-50 hover:opacity-100'}`}
          title="Italiano"
          data-testid="login-lang-it"
        >
          🇮🇹
        </button>
        <button
          onClick={() => toggleLanguage('en')}
          className={`text-2xl transition-all ${language === 'en' ? 'ring-2 ring-blue-500 rounded' : 'opacity-50 hover:opacity-100'}`}
          title="English"
          data-testid="login-lang-en"
        >
          🇬🇧
        </button>
      </div>
      <div
        className="hidden lg:block lg:w-1/2 bg-cover bg-center"
        style={{
          backgroundImage:
            'url(https://images.pexels.com/photos/32647132/pexels-photo-32647132.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
        }}
      />
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8" style={{ backgroundColor: '#F8F9FA' }}>
        <div className="w-full max-w-md">
          {step === 'credentials' ? (
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Label htmlFor="email" data-testid="login-email-label" style={{ color: '#161616' }}>{t('email')}</Label>
                <Input
                  id="email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required data-testid="login-email-input" className="mt-1"
                  style={{ color: '#161616', backgroundColor: '#FFFFFF' }}
                />
              </div>
              <div>
                <Label htmlFor="password" data-testid="login-password-label" style={{ color: '#161616' }}>{t('password')}</Label>
                <Input
                  id="password" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required data-testid="login-password-input" className="mt-1"
                  style={{ color: '#161616', backgroundColor: '#FFFFFF' }}
                />
              </div>
              <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="login-submit-button">
                {loading ? t('login_loading') : t('login_button')}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerify2fa} className="space-y-6" data-testid="login-2fa-form">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={28} style={{ color: '#0F62FE' }} />
                <h2 className="text-2xl font-bold" style={{ color: '#161616' }}>{t('login_2fa_title')}</h2>
              </div>
              <p className="text-sm" style={{ color: '#525252' }}>
                {t('login_2fa_prompt')}
              </p>
              <div>
                <Label htmlFor="totp-code" style={{ color: '#161616' }}>{t('login_2fa_code_label')}</Label>
                <Input
                  id="totp-code"
                  ref={codeInputRef}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  maxLength={6}
                  required
                  data-testid="login-2fa-code-input"
                  className="mt-1 text-2xl font-mono tracking-widest text-center"
                  style={{ color: '#161616', backgroundColor: '#FFFFFF' }}
                />
              </div>
              <Button type="submit" className="w-full btn-primary" disabled={loading || code.length !== 6} data-testid="login-2fa-submit">
                {loading ? t('login_loading') : t('login_2fa_button')}
              </Button>
              <button
                type="button"
                onClick={() => { setStep('credentials'); setPassword(''); setCode(''); }}
                className="w-full text-sm hover:underline"
                style={{ color: '#525252' }}
                data-testid="login-2fa-cancel"
              >
                {t('login_2fa_back')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
